<template>
  <div class="ma-4">
   
      <ProgramNav :programID="$route.params.programID" />
  
      <!-- Header Section -->
      <v-row class="ma-0 pa-0 header-section" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 32px 32px 0 32px; margin: 0;">
        <v-col cols="12" md="5">
          <div class="program-info-section">
            <v-avatar size="90" class="mr-5 program-avatar" style="background: rgba(255,255,255,0.2);">
              <v-icon size="45" color="white">mdi-home-group</v-icon>
            </v-avatar>
            <div class="flex-grow-1 d-flex flex-column justify-space-between">
              <div>
                <h1 class="text-h3 font-weight-bold white--text mb-3">
                  {{ program.Name || 'Program Dashboard' }}
                </h1>
                <p class="text-h6 white--text mb-5" style="opacity: 0.9; font-weight: 300;">
                  <v-icon small class="mr-2">mdi-map-marker</v-icon>
                  {{ program.Address }}, {{ program.City }}, {{ program.State }}
                </p>
              </div>
              
              <!-- Stats Row -->
              <div class="d-flex flex-wrap stats-row" style="gap: 40px;">
                <div class="stat-card">
                  <v-icon size="28" color="white" class="mb-2">mdi-account-group</v-icon>
                  <div class="stat-number">{{ staff.length }}</div>
                  <div class="stat-label">Staff Members</div>
                </div>
                
                <div class="stat-card">
                  <v-icon size="28" color="white" class="mb-2">mdi-calendar-clock</v-icon>
                  <div class="stat-number">{{ programSchedule.length }}</div>
                  <div class="stat-label">Today's Shifts</div>
                </div>
              </div>
            </div>
          </div>
        </v-col>
        
        <v-col cols="12" md="2">
          <div class="managers-section">
            <div class="managers-container">
              <!-- House Manager Card -->
              <div class="manager-card">
                <div class="manager-label">House Manager</div>
                <div class="manager-name">
                  {{ program.HouseManagerUser?.Name || program.HouseManagerName || 'Not assigned' }}
                </div>
                <div v-if="program.HouseManagerUser?.PhoneNumber" class="manager-phone">
                  <v-icon small class="mr-1">mdi-phone</v-icon>
                  {{ program.HouseManagerUser.PhoneNumber }}
                </div>
              </div>
              
              <!-- Assistant Manager Card -->
              <div class="manager-card">
                <div class="manager-label">Assistant Manager</div>
                <div class="manager-name">
                  {{ program.AssistantManagerUser?.Name || program.AssistantManagerName || 'Not assigned' }}
                </div>
                <div v-if="program.AssistantManagerUser?.PhoneNumber" class="manager-phone">
                  <v-icon small class="mr-1">mdi-phone</v-icon>
                  {{ program.AssistantManagerUser.PhoneNumber }}
                </div>
              </div>
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="1">

        </v-col>
        
        <v-col cols="12" md="4">
          <div class="clients-section">
            <div class="clients-header mb-3">
              <div class="d-flex align-center">
                <v-icon color="white" size="20" class="mr-2">mdi-account-group</v-icon>
                <span class="text-body-1 font-weight-medium white--text">Active Clients ({{ clients.length }})</span>
              </div>
            </div>
            
            <div class="clients-content">
              <div v-if="isLoadingClients" class="text-center py-3">
                <v-progress-circular indeterminate color="white" size="24" width="2"></v-progress-circular>
                <div class="mt-2 white--text text-caption">Loading clients...</div>
              </div>
              
              <div v-else-if="clientsError" class="text-center py-3">
                <v-icon color="error" size="24" class="mb-1">mdi-alert-circle</v-icon>
                <div class="white--text text-caption">{{ clientsError }}</div>
              </div>
              
              <div v-else>
                <div
                  v-for="client in clients.slice(0, 8)"
                  :key="client._id"
                  class="client-item clickable-list-item"
                  @click="goToClientInfo(client._id)"
                >
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2" style="background: rgba(255,255,255,0.2);">
                      <v-img v-if="client.Image" :src="client.Image" :alt="client.Name"></v-img>
                      <v-icon v-else color="white" size="16">mdi-account</v-icon>
                    </v-avatar>
                    
                    <div class="flex-grow-1">
                      <div class="client-name white--text font-weight-medium mb-1">
                        {{ client.Name }}
                      </div>
                      <div class="client-balances">
                        <template v-if="clientBalances[client._id]">
                          <div class="d-flex align-center">
                            <v-icon size="12" color="rgba(255,255,255,0.8)" class="mr-1">mdi-credit-card</v-icon>
                            <span class="white--text text-caption" style="opacity: 0.9;">EBT: ${{ clientBalances[client._id].EBT }}</span>
                            <v-icon size="12" color="rgba(255,255,255,0.8)" class="ml-3 mr-1">mdi-bank</v-icon>
                            <span class="white--text text-caption" style="opacity: 0.9;">Debit: ${{ clientBalances[client._id].DebitCard }}</span>
                          </div>
                        </template>
                        <template v-else>
                          <div class="d-flex align-center">
                            <v-progress-circular indeterminate color="white" size="12" width="2"></v-progress-circular>
                            <span class="ml-2 white--text text-caption" style="opacity: 0.8;">Loading balances...</span>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div v-if="clients.length === 0" class="text-center py-3">
                  <v-icon color="white" size="24" class="mb-1">mdi-account-group-outline</v-icon>
                  <div class="white--text text-caption">No clients found</div>
                  </div>
                
                <div v-if="clients.length > 8" class="text-center py-2">
                  <v-btn 
                    color="white" 
                    text-color="primary" 
                    x-small 
                    rounded 
                    class="font-weight-medium"
                  >
                    +{{ clients.length - 8 }} more
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>

        <v-row align="stretch" class="ma-0 pa-0">
          <!-- Program Info Card (Left Sidebar, Top) -->
          <v-col cols="12" md="3" class="d-flex flex-column">
            <!-- Today's Events Card -->
            <v-card class="mb-4 elevation-2 dashboard-card-max" rounded="lg">
              <v-card-title class="success white--text">
                <v-icon left>mdi-calendar-star</v-icon>
                Today's Events
                <v-spacer></v-spacer>
                <v-chip color="white" text-color="success" small>{{ todaysEvents.length }}</v-chip>
              </v-card-title>
              <v-card-text class="pt-4 dashboard-card-content-flex">
                <div v-if="isLoadingEvents" class="text-center py-4">
                  <v-progress-circular indeterminate color="deep-purple" size="30"></v-progress-circular>
                </div>
                <div v-else-if="eventsError" class="text-center py-4">
                  <v-icon color="error" size="32">mdi-alert-circle</v-icon>
                  <div class="mt-1 error--text text-caption">{{ eventsError }}</div>
                </div>
                <v-list v-else class="pa-0 dashboard-card-list-scroll">
                  <v-list-item v-for="event in todaysEvents" :key="event._id" class="mb-2 px-0">
                    <v-list-item-avatar size="32" class="mr-2">
                      <v-avatar size="32" color="success">
                        <v-icon color="white" size="16">mdi-calendar</v-icon>
                      </v-avatar>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="text-body-2 font-weight-medium">
                        {{ event.Event || 'Untitled Event' }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption text-muted">
                        {{ event.Client?.Name || 'Unknown Client' }} • {{ formatEventTime(event.StartDate) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item v-if="todaysEvents.length === 0" class="text-center py-4">
                    <v-icon color="grey" size="32">mdi-calendar-remove</v-icon>
                    <div class="mt-1 text-muted text-caption">No events for today</div>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
  
            <!-- Annual Documents Card -->
            <v-card class="mb-4 elevation-2 dashboard-card-max" rounded="lg">
              <v-card-title class="orange white--text">
                <v-icon left>mdi-file-document-alert</v-icon>
                Annual Documents
                <v-spacer></v-spacer>
                <v-chip color="white" text-color="orange" small>{{ expiringDocuments.length }}</v-chip>
              </v-card-title>
              <v-card-text class="pt-4 dashboard-card-content-flex">
                <div class="orange lighten-4 orange--text text--darken-2 pa-2 mb-3 rounded">
                  Expiring {{ new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) }}
                </div>
                <div v-if="isLoadingDocuments" class="text-center py-4">
                  <v-progress-circular indeterminate color="orange" size="30"></v-progress-circular>
                </div>
                <div v-else-if="documentsError" class="text-center py-4">
                  <v-icon color="error" size="32">mdi-alert-circle</v-icon>
                  <div class="mt-1 error--text text-caption">{{ documentsError }}</div>
                </div>
                <v-list v-else class="pa-0 dashboard-card-list-scroll">
                                      <v-list-item v-for="doc in expiringDocuments" :key="doc._id" class="mb-2 px-0 clickable-list-item" @click="goToAnnualDocument(doc)">
                      <v-list-item-avatar size="32" class="mr-2">
                        <v-avatar size="32" color="orange">
                          <v-icon color="white" size="16">mdi-file-document</v-icon>
                        </v-avatar>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title class="text-body-2 font-weight-medium">
                          {{ doc.Type || 'Unknown Document' }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-caption text-muted">
                          {{ doc.ClientInfo?.Name || 'Unknown Client' }} • Expires: {{ formatDate(doc.DateExpires) }}
                          <span v-if="getDaysUntilExpiry(doc.DateExpires) <= 7" class="error--text font-weight-bold">
                            ({{ getDaysUntilExpiry(doc.DateExpires) }} days)
                          </span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  <v-list-item v-if="expiringDocuments.length === 0" class="text-center py-4">
                    <v-icon color="success" size="32">mdi-check-circle</v-icon>
                    <div class="mt-1 success--text text-caption">No documents expiring this month</div>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>

  
          <!-- Main Content (Today's Shift Schedule) -->
          <v-col cols="12" md="6" class="d-flex flex-column">
            <v-card class="mb-4 elevation-2 dashboard-card-max flex-grow-1" rounded="lg">
              <v-card-title class="primary white--text">
                <v-icon left>mdi-calendar-clock</v-icon>
                Today's Shift Schedule
                <v-spacer></v-spacer>
                <v-chip color="white" text-color="primary" small>
                  {{ new Date().toLocaleDateString() }}
                </v-chip>
              </v-card-title>
              <v-card-text class="pt-4 dashboard-card-content-flex">
                <!-- Status Filter Buttons -->
                <div class="d-flex flex-wrap gap-2 mb-4">
                  <v-btn
                    :color="shiftFilter === 'all' ? 'primary' : 'grey lighten-2'"
                    :text-color="shiftFilter === 'all' ? 'white' : 'grey darken-2'"
                    @click="shiftFilter = 'all'"
                    class="mr-2"
                    x-small
                  >
                    All ({{ programSchedule.length }})
                  </v-btn>
                  <v-btn
                    :color="shiftFilter === 'upcoming' ? 'warning' : 'grey lighten-2'"
                    :text-color="shiftFilter === 'upcoming' ? 'white' : 'grey darken-2'"
                    @click="shiftFilter = 'upcoming'"
                    class="mr-2"
                    x-small
                  >
                    Upcoming ({{ upcomingShifts.length }})
                  </v-btn>
                  <v-btn
                    :color="shiftFilter === 'onShift' ? 'success' : 'grey lighten-2'"
                    :text-color="shiftFilter === 'onShift' ? 'white' : 'grey darken-2'"
                    @click="shiftFilter = 'onShift'"
                    class="mr-2"
                    x-small
                  >
                    On Shift ({{ onShiftShifts.length }})
                  </v-btn>
                  <v-btn
                    :color="shiftFilter === 'completed' ? 'grey' : 'grey lighten-2'"
                    :text-color="shiftFilter === 'completed' ? 'white' : 'grey darken-2'"
                    @click="shiftFilter = 'completed'"
                    x-small
                  >
                    Completed ({{ completedShifts.length }})
                  </v-btn>
                </div>
                
                <div v-if="isLoadingSchedule" class="text-center py-8">
                  <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
                  <div class="mt-2 text-muted">Loading schedule...</div>
                </div>
                <div v-else-if="scheduleError" class="text-center py-8">
                  <v-icon color="error" size="48">mdi-alert-circle</v-icon>
                  <div class="mt-2 error--text">{{ scheduleError }}</div>
                </div>
                <v-list v-else class="pa-0 dashboard-card-list-scroll">
                  <v-list-item 
                    v-for="shift in filteredShifts" 
                    :key="shift._id" 
                    class="mb-2 rounded-lg" 
                    :style="{ backgroundColor: getShiftBackgroundColor(shift) }"
                  >
                    <v-list-item-avatar size="40" class="mr-3">
                      <v-avatar size="40" :color="getShiftStatusColor(shift)">
                        <v-icon color="white" size="20">mdi-account</v-icon>
                      </v-avatar>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">
                        {{ shift.employee?.Name || 'No Employee' }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-muted">
                        {{ shift.ClientId?.[0]?.Name || 'No Client' }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <div class="text-right">
                        <div class="text-caption text-muted">{{ formatTime(shift.StartDate) }}</div>
                        <div class="text-caption text-muted">to {{ formatTime(shift.EndDate) }}</div>
                        <v-chip 
                          :color="getShiftStatusColor(shift)" 
                          text-color="white" 
                          x-small 
                          class="mt-1"
                        >
                          {{ getShiftStatus(shift) }}
                        </v-chip>
                      </div>
                    </v-list-item-action>
                  </v-list-item>
                  <v-list-item v-if="filteredShifts.length === 0" class="text-center py-8">
                    <v-list-item-content>
                      <v-icon color="grey" size="48">mdi-calendar-blank</v-icon>
                      <div class="mt-2 text-muted">
                        {{ shiftFilter === 'all' ? 'No shifts scheduled for today' : 
                           shiftFilter === 'upcoming' ? 'No upcoming shifts' :
                           shiftFilter === 'onShift' ? 'No active shifts' : 'No completed shifts' }}
                      </div>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
  
            <!-- Missing Reports Row -->
            <v-row>
              <v-col cols="12" md="6">
                <v-card class="mb-4 elevation-2 dashboard-card-tall d-flex flex-column" rounded="lg">
                  <v-card-title class="warning white--text">
                    <v-icon left>mdi-clipboard-alert</v-icon>
                    Missing MAR's
                    <v-spacer></v-spacer>
                    <v-chip color="white" text-color="warning" small>{{ missingMARs.length }}</v-chip>
                  </v-card-title>
                  <v-card-text class="pt-4 d-flex pt-4 dashboard-card-content-flex">
                    <div v-if="isLoadingMARs" class="text-center py-4">
                      <v-progress-circular indeterminate color="warning" size="30"></v-progress-circular>
                    </div>
                    <div v-else-if="marsError" class="text-center py-4">
                      <v-icon color="error" size="32">mdi-alert-circle</v-icon>
                      <div class="mt-1 error--text text-caption">{{ marsError }}</div>
                    </div>
                    <v-list v-else class="pa-0 dashboard-card-list-scroll flex-grow-1">
                      <v-list-item v-for="mar in missingMARs" :key="mar._id" class="mb-1 px-0 clickable-list-item" @click="goToMAR(mar.ClientID && mar.ClientID._id ? mar.ClientID._id : mar.ClientID)">
                        <v-list-item-avatar size="32" class="mr-2">
                          <v-avatar size="32" color="warning">
                            <v-icon color="white" size="16">mdi-account</v-icon>
                          </v-avatar>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title class="text-body-2">
                            {{ mar.ClientID && mar.ClientID.Name ? mar.ClientID.Name : 'Unknown Client' }}
                          </v-list-item-title>
                          <v-list-item-subtitle class="text-caption text-muted">
                            {{ formatDate(mar.Date) }}
                            <span v-if="mar.Medication && mar.Medication.Medication">
                              <br />
                              <strong>Medication:</strong> {{ mar.Medication.Medication }}
                            </span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item v-if="missingMARs.length === 0" class="text-center py-4">
                        <v-icon color="error" size="32">mdi-check-circle</v-icon>
                        <div class="mt-1 success--text text-caption">All MAR's completed</div>
                      </v-list-item>
                    </v-list>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="6">
                <v-card class="mb-4 elevation-2 dashboard-card-tall d-flex flex-column" rounded="lg">
                  <v-card-title class="error white--text">
                    <v-icon left>mdi-clipboard-text</v-icon>
                    Missing ADL's
                    <v-spacer></v-spacer>
                    <v-chip color="white" text-color="error" small>{{ missingADLs.length }}</v-chip>
                  </v-card-title>
                  <v-card-text class="pt-4 pt-4 dashboard-card-content-flex">
                    <div v-if="isLoadingADLs" class="text-center py-4">
                      <v-progress-circular indeterminate color="error" size="30"></v-progress-circular>
                    </div>
                    <div v-else-if="adlsError" class="text-center py-4">
                      <v-icon color="error" size="32">mdi-alert-circle</v-icon>
                      <div class="mt-1 error--text text-caption">{{ adlsError }}</div>
                    </div>
                    <v-list v-else class="pa-0 dashboard-card-list-scroll flex-grow-1">
                      <v-list-item v-for="adl in missingADLs" :key="adl._id" class="mb-1 px-0 clickable-list-item" @click="goToADL(adl.ClientID && adl.ClientID._id ? adl.ClientID._id : adl.ClientID)">
                        <v-list-item-avatar size="32" class="mr-2">
                          <v-avatar size="32" color="error">
                            <v-icon color="white" size="16">mdi-account</v-icon>
                          </v-avatar>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title class="text-body-2">
                            {{ adl.ClientID && adl.ClientID.Name ? adl.ClientID.Name : 'Unknown Client' }}
                          </v-list-item-title>
                          <v-list-item-subtitle class="text-caption text-muted">
                            {{ formatDate(adl.Date) }}
                            <span v-if="adl.Service && adl.Service.ServiceItem">
                              <br />
                              <strong>ADL:</strong> {{ adl.Service.ServiceItem }}
                            </span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item v-if="missingADLs.length === 0" class="text-center py-4">
                        <v-icon color="success" size="32">mdi-check-circle</v-icon>
                        <div class="mt-1 success--text text-caption">All ADL's completed</div>
                      </v-list-item>
                    </v-list>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
  
          <!-- Sidebar (Staff Only) -->
          <v-col cols="12" md="3" class="d-flex flex-column">
            <!-- Staff Card -->
            <v-card class="mb-4 elevation-2 dashboard-card-max" rounded="lg">
              <v-card-title class="info white--text">
                <v-icon left>mdi-account-tie</v-icon>
                Staff
                <v-spacer></v-spacer>
                <v-chip color="white" text-color="info" small>{{ staff.length }}</v-chip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon 
                      v-bind="attrs" 
                      v-on="on" 
                      small 
                      class="ml-2" 
                      color="white" 
                      style="opacity: 0.8;"
                    >
                      mdi-information
                    </v-icon>
                  </template>
                  <span>Showing: Program Manager, House Manager, Assistant Manager, Shift Lead, Direct Care Staff</span>
                </v-tooltip>
              </v-card-title>
              <v-card-text class="pt-4 dashboard-card-content-flex">
                <div v-if="isLoadingStaff" class="text-center py-4">
                  <v-progress-circular indeterminate color="info" size="30"></v-progress-circular>
                </div>
                <div v-else-if="staffError" class="text-center py-4">
                  <v-icon color="error" size="32">mdi-alert-circle</v-icon>
                  <div class="mt-1 error--text text-caption">{{ staffError }}</div>
                </div>
                <v-list v-else class="pa-0 dashboard-card-list-scroll">
                  <v-list-item
                    v-for="user in staff"
                    :key="user._id"
                    class="mb-2 px-0 clickable-list-item"
                    @click="goToStaffInfo(user._id)"
                  >
                    <v-list-item-avatar size="40" class="mr-3">
                      <v-avatar size="40" color="info">
                        <v-img v-if="user.Image" :src="user.Image" :alt="user.Name"></v-img>
                        <v-icon v-else color="white" size="20">mdi-account-tie</v-icon>
                      </v-avatar>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="text-body-1 font-weight-medium">
                        {{ user.Name }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption text-muted">
                        {{ user.Role || 'No Role' }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item v-if="staff.length === 0" class="text-center py-4">
                    <v-icon color="grey" size="32">mdi-account-tie-outline</v-icon>
                    <div class="mt-1 text-muted text-caption">No staff found</div>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
            
            <!-- Utah Incidents Card -->
            <v-card v-if="showUtahIncidents" class="mb-4 elevation-2 dashboard-card-tall d-flex flex-column" rounded="lg">
              <v-card-title class="deep-purple white--text">
                <v-icon left>mdi-alert-circle</v-icon>
                Unsigned Incidents
                <v-spacer></v-spacer>
                <v-chip color="white" text-color="deep-purple" small>{{ unsignedUtahIncidents.length }}</v-chip>
              </v-card-title>
              <v-card-text class="pt-4 dashboard-card-content-flex">
                <div v-if="isLoadingUtahIncidents" class="text-center py-4">
                  <v-progress-circular indeterminate color="deep-purple" size="30"></v-progress-circular>
                </div>
                <div v-else-if="utahIncidentsError" class="text-center py-4">
                  <v-icon color="error" size="32">mdi-alert-circle</v-icon>
                  <div class="mt-1 error--text text-caption">{{ utahIncidentsError }}</div>
                </div>
                <v-list v-else class="pa-0 dashboard-card-list-scroll flex-grow-1">
                  <v-list-item v-for="incident in unsignedUtahIncidents" :key="incident._id" class="mb-1 px-0 clickable-list-item" @click="goToUtahIncident(incident._id)">
                    <v-list-item-avatar size="32" class="mr-2">
                      <v-avatar size="32" color="deep-purple">
                        <v-icon color="white" size="16">mdi-account</v-icon>
                      </v-avatar>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="text-body-2">
                        {{ incident.ClientID && incident.ClientID.Name ? incident.ClientID.Name : 'Unknown Client' }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption text-muted">
                        {{ formatDate(incident.IncidentDateTime) }}
                        <span v-if="incident.Type">
                          <br />
                          <strong>Type:</strong> {{ incident.Type }}
                        </span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item v-if="unsignedUtahIncidents.length === 0" class="text-center py-4">
                    <v-icon color="success" size="32">mdi-check-circle</v-icon>
                    <div class="mt-1 success--text text-caption">All incidents signed</div>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
            
            <!-- Nevada Incidents Card -->
            <v-card v-if="showNevadaIncidents" class="mb-4 elevation-2 dashboard-card-tall d-flex flex-column" rounded="lg">
              <v-card-title class="indigo white--text">
                <v-icon left>mdi-alert-circle</v-icon>
                Unsigned Incidents
                <v-spacer></v-spacer>
                <v-chip color="white" text-color="indigo" small>{{ unsignedNevadaIncidents.length }}</v-chip>
              </v-card-title>
              <v-card-text class="pt-4 dashboard-card-content-flex">
                <div v-if="isLoadingNevadaIncidents" class="text-center py-4">
                  <v-progress-circular indeterminate color="indigo" size="30"></v-progress-circular>
                </div>
                <div v-else-if="nevadaIncidentsError" class="text-center py-4">
                  <v-icon color="error" size="32">mdi-alert-circle</v-icon>
                  <div class="mt-1 error--text text-caption">{{ nevadaIncidentsError }}</div>
                </div>
                <v-list v-else class="pa-0 dashboard-card-list-scroll flex-grow-1">
                  <v-list-item v-for="incident in unsignedNevadaIncidents" :key="incident._id" class="mb-1 px-0 clickable-list-item" @click="goToNevadaIncident(incident._id)">
                    <v-list-item-avatar size="32" class="mr-3">
                      <v-avatar size="32" color="indigo">
                        <v-icon color="white" size="16">mdi-account</v-icon>
                      </v-avatar>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="text-body-2">
                        {{ incident.ClientID && incident.ClientID.Name ? incident.ClientID.Name : 'Unknown Client' }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption text-muted">
                        {{ formatDate(incident.IncidentDate) }}
                        <span v-if="incident.Type">
                          <br />
                          <strong>Type:</strong> {{ incident.Type }}
                        </span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item v-if="unsignedNevadaIncidents.length === 0" class="text-center py-4">
                    <v-icon color="success" size="32">mdi-check-circle</v-icon>
                    <div class="mt-1 success--text text-caption">All incidents signed</div>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
     
    
  </div>
  </template>
  
  <script>
  import axios from 'axios';
  import ProgramNav from "@/components/ProgramNav";
  const url = require("@/client_config/config").url;
  
  export default {
    name: 'ProgramDashboard',
    components: {
      ProgramNav
    },
    data() {
      return {
        program: {},
        programSchedule: [],
        missingMARs: [],
        missingADLs: [],
        clients: [],
        clientBalances: {},
        staff: [],
        todaysEvents: [],
        isLoadingEvents: false,
        eventsError: null,
        isLoadingProgram: false,
        programError: null,
        isLoadingStaff: false,
        staffError: null,
        isLoadingClients: false,
        clientsError: null,
        isLoadingSchedule: false,
        scheduleError: null,
        isLoadingADLs: false,
        adlsError: null,
        isLoadingMARs: false,
        marsError: null,
        shiftFilter: 'all',
        upcomingShifts: [],
        onShiftShifts: [],
        completedShifts: [],
        expiringDocuments: [],
        isLoadingDocuments: false,
        documentsError: null,
        unsignedUtahIncidents: [],
        unsignedNevadaIncidents: [],
        isLoadingUtahIncidents: false,
        isLoadingNevadaIncidents: false,
        utahIncidentsError: null,
        nevadaIncidentsError: null,
      };
    },
    computed: {
      filteredShifts() {
        switch (this.shiftFilter) {
          case 'upcoming':
            return this.upcomingShifts;
          case 'onShift':
            return this.onShiftShifts;
          case 'completed':
            return this.completedShifts;
          default:
            return this.programSchedule;
        }
      },
      
      userState() {
        const state = localStorage.getItem('userState');
        return state || 'UT';
      },
      
      showUtahIncidents() {
        return this.userState === 'UT' || this.userState === 'Utah';
      },
      
      showNevadaIncidents() {
        return this.userState === 'NV' || this.userState === 'Nevada';
      },
    },
    created() {
      this.fetchProgramInfo();
      this.fetchProgramSchedule();
      this.fetchMissingMARs();
      this.fetchMissingADLs();
      this.fetchClients();
      this.fetchTodaysEvents();
      this.fetchExpiringDocuments();
      if (this.showUtahIncidents) {
        this.fetchUnsignedUtahIncidents();
      }
      if (this.showNevadaIncidents) {
        this.fetchUnsignedNevadaIncidents();
      }
    },
    watch: {
      program(newVal) {
        if (newVal && newVal.Name) {
          this.fetchStaff();
          this.fetchClients();
          this.fetchProgramSchedule();
          this.fetchMissingADLs();
          this.fetchMissingMARs();
          this.fetchTodaysEvents();
          this.fetchExpiringDocuments();
          if (this.showUtahIncidents) {
            this.fetchUnsignedUtahIncidents();
          }
          if (this.showNevadaIncidents) {
            this.fetchUnsignedNevadaIncidents();
          }
        }
      },
      '$route.params.programID': {
        immediate: true,
        handler() {
          this.program = {};
          this.programSchedule = [];
          this.missingMARs = [];
          this.missingADLs = [];
          this.clients = [];
          this.clientBalances = {};
          this.staff = [];
          this.todaysEvents = [];
          this.unsignedUtahIncidents = [];
          this.unsignedNevadaIncidents = [];
          this.fetchProgramInfo();
          // The rest will be triggered by the program watcher
        }
      }
    },
    methods: {
      async fetchProgramInfo() {
        this.isLoadingProgram = true;
        this.programError = null;
        try {
          const programID = this.$route.params.programID;
          const response = await axios.get(`${url}/units/${programID}`);
          this.program = response.data.unit || {};
        } catch (error) {
          this.programError = error.response?.data?.msg || error.message;
          this.program = {};
        } finally {
          this.isLoadingProgram = false;
        }
      },
      async fetchProgramSchedule() {
        this.isLoadingSchedule = true;
        this.scheduleError = null;
        try {
          const programName = this.program.Name;
          if (!programName) throw new Error('Program name not loaded');
          const response = await axios.get(`${url}/shifts/program/${encodeURIComponent(programName)}/today`);
          this.programSchedule = response.data.shifts || [];
          
          // Categorize shifts by status
          this.categorizeShifts();
        } catch (error) {
          this.scheduleError = error.response?.data?.msg || error.message;
          this.programSchedule = [];
          this.upcomingShifts = [];
          this.onShiftShifts = [];
          this.completedShifts = [];
        } finally {
          this.isLoadingSchedule = false;
        }
      },
      categorizeShifts() {
        const now = new Date();
        
        this.upcomingShifts = this.programSchedule.filter(shift => {
          const startDate = new Date(shift.StartDate);
          return startDate > now;
        });
        
        this.onShiftShifts = this.programSchedule.filter(shift => {
          const startDate = new Date(shift.StartDate);
          const endDate = new Date(shift.EndDate);
          return startDate <= now && endDate >= now;
        });
        
        this.completedShifts = this.programSchedule.filter(shift => {
          const endDate = new Date(shift.EndDate);
          return endDate < now;
        });
      },
      getShiftStatus(shift) {
        const now = new Date();
        const startDate = new Date(shift.StartDate);
        const endDate = new Date(shift.EndDate);
        
        if (startDate > now) {
          return 'Upcoming';
        } else if (startDate <= now && endDate >= now) {
          return 'On Shift';
        } else {
          return 'Completed';
        }
      },
      getShiftStatusColor(shift) {
        const status = this.getShiftStatus(shift);
        switch (status) {
          case 'Upcoming':
            return 'warning';
          case 'On Shift':
            return 'success';
          case 'Completed':
            return 'grey';
          default:
            return 'primary';
        }
      },
      getShiftBackgroundColor(shift) {
        const status = this.getShiftStatus(shift);
        switch (status) {
          case 'Upcoming':
            return '#fff3e0'; // Light orange background
          case 'On Shift':
            return '#e8f5e8'; // Light green background
          case 'Completed':
            return '#f5f5f5'; // Light grey background
          default:
            return '#f8f9fa'; // Default light background
        }
      },
      async fetchMissingMARs() {
        this.isLoadingMARs = true;
        this.marsError = null;
        try {
          const programName = this.program.Name;
          if (!programName) throw new Error('Program name not loaded');
          const response = await axios.get(`${url}/mar/by-program/${encodeURIComponent(programName)}/unsigned`);
          this.missingMARs = response.data.mars || [];
        } catch (error) {
          this.marsError = error.response?.data?.msg || error.message;
          this.missingMARs = [];
        } finally {
          this.isLoadingMARs = false;
        }
      },
      async fetchMissingADLs() {
        this.isLoadingADLs = true;
        this.adlsError = null;
        try {
          const programName = this.program.Name;
          if (!programName) throw new Error('Program name not loaded');
          const response = await axios.get(`${url}/adl/unsigned/program/${encodeURIComponent(programName)}`);
          this.missingADLs = response.data.adls || [];
        } catch (error) {
          this.adlsError = error.response?.data?.msg || error.message;
          this.missingADLs = [];
        } finally {
          this.isLoadingADLs = false;
        }
      },
      async fetchClients() {
        this.isLoadingClients = true;
        this.clientsError = null;
        try {
          const programName = this.program.Name;
          if (!programName) throw new Error('Program name not loaded');
          const response = await axios.get(`${url}/clients/house/${encodeURIComponent(programName)}`);
          this.clients = response.data.clients || [];
          // Fetch balances for each client (EBT, Debit)
          this.fetchClientBalances();
        } catch (error) {
          this.clientsError = error.response?.data?.msg || error.message;
          this.clients = [];
        } finally {
          this.isLoadingClients = false;
        }
      },
      async fetchClientBalances() {
        try {
          const requests = (this.clients || []).map((client) =>
            axios
              .get(`${url}/finances/dashboard/${client._id}`)
              .then((res) => ({ id: client._id, data: res.data && res.data.data }))
              .catch(() => ({ id: client._id, data: null }))
          );
          const results = await Promise.all(requests);
          results.forEach((result) => {
            if (result && result.data) {
              const ebt = result.data.EBT || '0.00';
              const debit = result.data.DebitCard || '0.00';
              this.$set(this.clientBalances, result.id, { EBT: ebt, DebitCard: debit });
            }
          });
        } catch (e) {
          // Silently ignore; per-client errors handled above
        }
      },
      async fetchStaff() {
        this.isLoadingStaff = true;
        this.staffError = null;
        try {
          const programName = this.program.Name;
          if (!programName) throw new Error('Program name not loaded');
          // Use the new program dashboard specific route
          const response = await axios.get(`${url}/users/program-dashboard/${encodeURIComponent(programName)}`);
          this.staff = response.data.users || [];
        } catch (error) {
          this.staffError = error.response?.data?.msg || error.message;
          this.staff = [];
        } finally {
          this.isLoadingStaff = false;
        }
      },
      async fetchTodaysEvents() {
        this.isLoadingEvents = true;
        this.eventsError = null;
        try {
          const programId = this.program._id || this.$route.params.programID;
          if (!programId) throw new Error('Program ID not loaded');
          
          console.log('Fetching events for programId:', programId);
          
          // Try the main calendar route first
          let response;
          try {
            response = await axios.get(`${url}/calendar`, {
              params: { 
                programIds: programId,
                limit: -1
              }
            });
            console.log('Main route response:', response.data);
          } catch (mainError) {
            console.log('Main route failed, trying direct query:', mainError);
            // Fallback: try to get all events and filter client-side
            response = await axios.get(`${url}/calendar`, {
              params: { limit: -1 }
            });
            console.log('All events response:', response.data);
          }
          
          // Check the actual data structure - try different possible keys
          let events = [];
          if (response.data && response.data.docs) {
            // Handle paginated response
            events = Array.isArray(response.data.docs) ? response.data.docs : [];
          } else if (response.data && response.data.calendar) {
            events = Array.isArray(response.data.calendar) ? response.data.calendar : [];
          } else if (response.data && response.data.events) {
            events = Array.isArray(response.data.events) ? response.data.events : [];
          } else if (Array.isArray(response.data)) {
            events = response.data;
          } else {
            console.log('No events found in response structure');
            events = [];
          }
          
          // If we got all events, filter by program ID client-side
          if (events.length > 0 && !response.data.docs && !response.data.calendar) {
            events = events.filter(ev => ev.Program && ev.Program._id === programId);
            console.log('Filtered by program ID client-side:', events);
          }
          
          console.log('Events array:', events);
          console.log('Events array length:', events.length);
          
          // Filter for today's events only
          const today = new Date();
          const todayString = today.toISOString().split('T')[0]; // Get YYYY-MM-DD format
          
          console.log('Today string:', todayString);
          
          const isToday = (dateStr) => {
            if (!dateStr) return false;
            const eventDate = new Date(dateStr);
            const eventDateString = eventDate.toISOString().split('T')[0];
            console.log('Comparing:', eventDateString, 'with', todayString);
            return eventDateString === todayString;
          };
          
          this.todaysEvents = events.filter(ev => {
            console.log('Checking event:', ev);
            console.log('Event StartDate:', ev.StartDate);
            return ev.StartDate && isToday(ev.StartDate);
          });
          
          console.log('Final filtered events:', this.todaysEvents);
          
        } catch (error) {
          console.error('Error fetching events:', error);
          this.eventsError = error.response?.data?.msg || error.message;
          this.todaysEvents = [];
        } finally {
          this.isLoadingEvents = false;
        }
      },
      async fetchExpiringDocuments() {
        this.isLoadingDocuments = true;
        this.documentsError = null;
        try {
          const programName = this.program.Name;
          if (!programName) throw new Error('Program name not loaded');
          const response = await axios.get(`${url}/annualDocs/expiring/program/${encodeURIComponent(programName)}`);
          this.expiringDocuments = response.data.documents || [];
        } catch (error) {
          this.documentsError = error.response?.data?.msg || error.message;
          this.expiringDocuments = [];
        } finally {
          this.isLoadingDocuments = false;
        }
      },
      
      async fetchUnsignedUtahIncidents() {
        this.isLoadingUtahIncidents = true;
        this.utahIncidentsError = null;
        try {
          const programName = this.program.Name;
          if (!programName) throw new Error('Program name not loaded');
          const response = await axios.get(`${url}/incidents/unsigned/program/${encodeURIComponent(programName)}`);
          this.unsignedUtahIncidents = response.data.incidents || [];
        } catch (error) {
          this.utahIncidentsError = error.response?.data?.msg || error.message;
          this.unsignedUtahIncidents = [];
        } finally {
          this.isLoadingUtahIncidents = false;
        }
      },
      
      async fetchUnsignedNevadaIncidents() {
        this.isLoadingNevadaIncidents = true;
        this.nevadaIncidentsError = null;
        try {
          const programName = this.program.Name;
          if (!programName) throw new Error('Program name not loaded');
          const response = await axios.get(`${url}/nevadaIncidents/unsigned/program/${encodeURIComponent(programName)}`);
          this.unsignedNevadaIncidents = response.data.incidents || [];
        } catch (error) {
          this.nevadaIncidentsError = error.response?.data?.msg || error.message;
          this.unsignedNevadaIncidents = [];
        } finally {
          this.isLoadingNevadaIncidents = false;
        }
      },
      formatDateTime(date) {
        if (!date) return '';
        const d = new Date(date);
        return d.toLocaleString();
      },
      formatTime(date) {
        if (!date) return '';
        const d = new Date(date);
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      },
      formatDate(date) {
        if (!date) return '';
        const d = new Date(date);
        return d.toLocaleDateString();
      },
      formatEventTime(date) {
        if (!date) return '';
        const d = new Date(date);
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      },
      getDaysUntilExpiry(dateString) {
        if (!dateString) return Infinity;
        const expiryDate = new Date(dateString);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Normalize today to start of day
        expiryDate.setHours(0, 0, 0, 0); // Normalize expiry to start of day
  
        const timeDiff = expiryDate.getTime() - today.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
        return daysDiff;
      },
      goToClientInfo(clientId) {
        this.$router.push(`/ClientInfo/${clientId}`);
      },
      goToStaffInfo(staffId) {
        this.$router.push(`/StaffInfo/${staffId}`);
      },
      goToProgramDocuments() {
        this.$router.push(`/ProgramDocuments/${this.$route.params.programID}`);
      },
      goToMAR(clientId) {
        this.$router.push(`/mar/${clientId}/1`);
      },
      goToADL(clientId) {
        this.$router.push(`/adl/${clientId}`);
      },
      
      goToUtahIncident(incidentId) {
        // Navigate to the Utah incident update page
        // We need to get the client ID from the incident to navigate properly
        const incident = this.unsignedUtahIncidents.find(inc => inc._id === incidentId);
        if (incident && incident.ClientID) {
          const clientId = incident.ClientID._id || incident.ClientID;
          this.$router.push(`/incidents/${clientId}/update-incident/${incidentId}`);
        }
      },
      
      goToNevadaIncident(incidentId) {
        // Navigate to the Nevada incident update page
        // We need to get the client ID from the incident to navigate properly
        const incident = this.unsignedNevadaIncidents.find(inc => inc._id === incidentId);
        if (incident && incident.ClientID) {
          const clientId = incident.ClientID._id || incident.ClientID;
          this.$router.push(`/nvIncidents/${clientId}/update-incident/${incidentId}`);
        }
      },
      
      goToAnnualDocument(doc) {
        // Navigate to the annual document view page
        // We need to get the client ID from the document to navigate properly
        if (doc && doc.ClientInfo) {
          const clientId = doc.ClientInfo._id || doc.ClientInfo;
          this.$router.push(`/annualDocs/${clientId}/update/${doc._id}`);
        }
      },
    },
  };
  </script>
  
  <style scoped>
  .v-card {
    transition: all 0.3s ease;
  }
  
  .v-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
  }
  
  .v-list-item {
    transition: all 0.2s ease;
  }
  
  .v-list-item:hover {
    background-color: rgba(0,0,0,0.04) !important;
  }
  
  .text-muted {
    color: rgba(0,0,0,0.6) !important;
  }
  
  /* Custom scrollbar for lists */
  .v-list::-webkit-scrollbar {
    width: 6px;
  }
  
  .v-list::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  .v-list::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  
  .v-list::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
  
  .clickable-list-item {
    cursor: pointer;
    transition: background 0.2s;
  }
  
  .clickable-list-item:hover {
    background: #e3f2fd !important;
  }
  
  .dashboard-card-max {
    max-height: 300px;
    min-height: 300px;
    display: flex;
    flex-direction: column;
  }
  
  .dashboard-card-content-flex {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  
  .dashboard-card-list-scroll {
    overflow-y: auto;
    flex: 1 1 auto;
    min-height: 0;
  }
  
  .dashboard-card-tall {
    min-height: 300px;
    max-height: 300px;
    display: flex;
    flex-direction: column;
  }
  
  /* Header Section Styles */
  .manager-card {
    background: rgba(255, 255, 255, 0.1);
    padding: 16px 20px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
  }
  
  .manager-card:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.2);
  }
  
  .manager-label {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }
  
  .manager-name {
    color: white;
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 4px;
  }
  
  .manager-phone {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.875rem;
    display: flex;
    align-items: center;
  }
  
  .stat-card {
    background: rgba(255, 255, 255, 0.1);
    padding: 20px 24px;
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    text-align: center;
    transition: all 0.3s ease;
    min-width: 140px;
  }
  
  .stat-card:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.2);
  }
  
  .stat-number {
    color: white;
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 4px;
    text-shadow: 0 2px 4px rgba(0,0,0,0.3);
  }
  
  .stat-label {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.875rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  /* Enhanced Client Card Styles */
  .clickable-list-item:hover {
    background: #e0e0e0 !important;
    transform: translateX(4px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  
  /* Flat Design Styles */
  .flat-card {
    background: white !important;
    box-shadow: none !important;
    border: 1px solid #e0e0e0;
  }
  
  .flat-title {
    background: #f5f5f5 !important;
    border-bottom: 1px solid #e0e0e0;
  }
  
  /* Header Section Responsive Styles */
  .header-section {
    min-height: auto;
  }
  
  .program-info-section {
    display: flex;
    align-items: flex-start;
    min-height: 320px;
    height: auto;
  }
  
  .program-avatar {
    flex-shrink: 0;
  }
  
  .stats-row {
    margin-top: auto;
  }
  
  .managers-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-height: 320px;
    height: auto;
    justify-content: center;
  }
  
  /* Clients Section Styles */
  .clients-section {
    min-height: 320px;
    height: auto;
    display: flex;
    flex-direction: column;
  }
  
  .clients-header {
    padding: 16px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    flex-shrink: 0;
  }
  
  .clients-content {
    flex: 1;
    padding-right: 8px;
    overflow-y: auto;
    max-height: 280px;
  }
  
  .client-item {
    padding: 12px 16px;
    margin-bottom: 8px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    transition: all 0.2s ease;
    cursor: pointer;
  }
  
  .client-item:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  
  .client-name {
    font-size: 0.875rem;
    font-weight: 600;
  }
  
  .client-balances {
    font-size: 0.75rem;
  }
  
  /* Mobile Responsive Adjustments */
  @media (max-width: 960px) {
    .header-section {
      padding: 24px 16px 0 16px !important;
    }
    
    .program-info-section {
      flex-direction: column;
      align-items: center;
      text-align: center;
      min-height: auto;
      padding-bottom: 24px;
    }
    
    .program-avatar {
      margin-right: 0 !important;
      margin-bottom: 16px;
    }
    
    .stats-row {
      justify-content: center;
      margin-top: 24px;
    }
    
    .managers-container {
      min-height: auto;
      padding: 16px 0;
    }
    
    .clients-section {
      min-height: auto;
      padding-top: 24px;
    }
    
    .clients-content {
      max-height: 200px;
    }
  }
  
  @media (max-width: 600px) {
    .header-section {
      padding: 16px 12px 0 12px !important;
    }
    
    .program-info-section h1 {
      font-size: 1.75rem !important;
    }
    
    .program-info-section p {
      font-size: 1rem !important;
    }
    
    .stat-card {
      min-width: 120px;
      padding: 16px 20px;
    }
    
    .stat-number {
      font-size: 1.5rem !important;
    }
    
    .manager-card {
      padding: 12px 16px;
    }
    
    .clients-content {
      max-height: 180px;
    }
  }
  </style>
  